import React from 'react';
import { useStyletron, styled } from '@activebrands/core-web/libs/styletron';
import responsiveTextStyles from '@activebrands/core-web/utils/responsive-text-styles';
import fm from 'format-message'; 
  
interface SkipLinkItem {
  label: string;
  target: string;
}

interface SkipLinksMenuProps {
  links?: SkipLinkItem[];
  fontKeys?: string | string[];
  $style?: Record<string, any>;
  linkStyle?: Record<string, any>;
}

// Default skip links
const SKIP_LINKS: SkipLinkItem[] = [
  { label: fm('Skip to main content'), target: '#main' },
  { label: fm('Skip to primary navigation'), target: '#primary-nav' },
  { label: fm('Skip to search'), target: '#search-header' },
  { label: fm('Skip to footer'), target: '#footer' },
];

// Default branding style for skip link
// This can be overridden by the linkStyle
const defaultLinkCssBranding =  {
    background: "var(--color-bg)", // default brand color
    color: "var(--color-text)", // default brand text color
    border: "1px solid var(--color-border)",  // default brand border color
    display: "block",
    ':focus': {
      outline: "2px solid var(--color-link)",
      border:  "1px solid var(--color-link)",
      color: "var(--color-link)",
    }
} ;

const MenuWrapper = styled('nav', {
    position: "absolute", 
    top: "-250px",
    left: "0",
    zIndex: "var(--zindex-foreground)",
    backgroundColor: "var(--color-bg)", // default brand color
    color: "var(--color-text)", // default brand text color
});

const MenuList = styled('ul', {
    listStyle: "none",
    display: "flex",
    flexDirection: "column",
    gap: ".5rem",
});
 
const SkipLinksMenu: React.FC<SkipLinksMenuProps> = ({$style = {}, links, fontKeys = '', linkStyle={}}) => {
  const [css] = useStyletron();
  const skiplinks = links || SKIP_LINKS;
  const textStyles = responsiveTextStyles(fontKeys) || {};
  
  // CSS for focusable elements, ensures the menu is focusable and has a default focus style
  // This is important for accessibility, can be overridden by linkStyle prop
  const defaultFocusCss = { 
    ':focus-within': {top: "0"},
    ':focus': {
      top: "0",
      outline: "2px solid var(--color-link)", // default brand focus outline color
    },  
  }

  return (
    <MenuWrapper
      aria-label={fm("Skip links menu")}
      className={css({...defaultFocusCss, ...$style})}
      tabIndex={0}
    >
      <MenuList tabindex={-1} role="menu" aria-label={fm("page sections")}>
        {skiplinks.map((link) => (
          <li key={link.target} role="none"> 
            <a role="menuitem" href={link.target} className={css({...defaultLinkCssBranding, ...textStyles, ...linkStyle })}>
              {link.label}
            </a>
          </li>
        ))}
      </MenuList>
    </MenuWrapper>
  );
};

export default SkipLinksMenu;


